import { Box, SvgIcon, Tooltip } from '@mui/material';
import * as R from 'ramda';
import React from 'react';
import TextField from '@mui/material/TextField';
import { formatNumber } from 'utils/helpers/format';

const CustomCheckIcon = props => (
  <SvgIcon {...props} viewBox="0 0 20 20">
    <circle cx="10" cy="10" fill="#BBDEFB" r="10" />
    <path
      clipRule="evenodd"
      d="M8.03367 13.2629L5.42717 10.7116C5.28951 10.5751 5.10177 10.4984 4.90588 10.4984C4.70998 10.4984 4.52224 10.5751 4.38458 10.7116C4.09414 10.9959 4.09414 11.4478 4.38458 11.7321L7.50492 14.7865C7.79536 15.0707 8.26453 15.0707 8.55497 14.7865L16.4489 7.06676C16.7394 6.78247 16.7394 6.33051 16.4489 6.04622C16.3113 5.90979 16.1235 5.83301 15.9276 5.83301C15.7317 5.83301 15.544 5.90979 15.4063 6.04622L8.03367 13.2629Z"
      fill="#0D47A1"
      fillRule="evenodd"
    />
  </SvgIcon>
);

const getFormattedNumber = value => {
  const trimmedNumber = String(value).replace(/[^0-9]/g, '');
  if (R.isEmpty(trimmedNumber) || R.isNil(trimmedNumber) || trimmedNumber === '0') return '';
  return formatNumber(trimmedNumber);
};

// Columns definition for the grid
const toLocationColumns = [
  {
    align: 'left',
    editable: true,
    field: 'transferVolume',
    headerAlign: 'left',
    headerName: 'Transfer Volume',
    renderCell: params => {
      const isFooter = params.id === 'auto-generated-group-footer-root';
      if (isFooter) {
        return params.value ? formatNumber(params.value) : '0';
      }
      return params.value ? (
        formatNumber(params.value)
      ) : (
        <span style={{ color: '#aaa' }}>Volume</span>
      );
    },
    renderEditCell: ({
      api,
      field,
      id,
      value,
      row,
      isVolumeExceeded,
      maxAllowedVolume,
      totalTransferVolume,
    }) => {
      const hasAvailableVolume = row.availableVolume > 0;
      const hasError =
        value !== null && value !== '' && (value <= 0 || !Number.isInteger(Number(value)));
      const cellIsEmpty = value === null || value === '' || value === undefined;
      const isDisabled = cellIsEmpty && isVolumeExceeded;
      const currentValue = Number(value) || 0;
      const otherCellsTotal = totalTransferVolume - currentValue;
      const wouldExceedLimit = otherCellsTotal + currentValue > maxAllowedVolume;

      return (
        <TextField
          autoFocus
          disabled={isDisabled}
          error={hasError}
          onChange={event => {
            if (!isDisabled) {
              const rawValue = event.target.value.replace(/[^0-9]/g, '');
              const newValue = rawValue;
              const newValueNum = Number(rawValue) || 0;
              const newTotal = otherCellsTotal + newValueNum;

              api.setEditCellValue(
                {
                  field,
                  id,
                  metadata: {
                    wouldExceedLimit: newTotal > maxAllowedVolume,
                  },
                  value: newValue,
                },
                event
              );
            }
          }}
          onFocus={event => {
            api.setEditCellValue(
              {
                field,
                id,
                value: row.transferVolume,
              },
              event
            );
          }}
          slotProps={{
            input: {
              autoFocus: true,
              inputProps: {
                min: 1,
                onClick: event => {
                  if (!isDisabled) {
                    event.stopPropagation();
                    event.target.select();
                  }
                },
              },
              readOnly: isDisabled,
            },
          }}
          sx={{
            '& fieldset': { border: 'none' },
            '& input': { p: '5px' },
            backgroundColor:
              wouldExceedLimit || totalTransferVolume > maxAllowedVolume
                ? '#FFE0E0'
                : hasError
                ? '#FFEBEE'
                : !hasAvailableVolume || isDisabled
                ? 'action.disabledBackground'
                : 'secondary.light',
            cursor: isDisabled ? 'not-allowed' : 'auto',
            height: '100%',
            opacity: isDisabled ? 0.6 : 1,
            pointerEvents: isDisabled ? 'none' : 'auto',
            width: '100%',
          }}
          tabIndex={isDisabled ? -1 : undefined}
          type="string"
          value={getFormattedNumber(value)}
        />
      );
    },
    type: 'number',
    valueSetter: ({ value, row }) => {
      if (row.transferVolume === null && (value === '' || value === null)) {
        return { ...row };
      }
      if (value === '' || value === null || value === undefined) {
        return { ...row, transferVolume: null };
      }
      const newValue = parseInt(value, 10);
      if (Number.isNaN(newValue) || newValue < 0) {
        return { ...row };
      }
      if (newValue === 0) {
        return { ...row, transferVolume: null };
      }

      return { ...row, transferVolume: newValue };
    },
    width: 200,
  },
  { field: 'locationIdName', headerName: 'Location ID', sortable: true, width: 200 },
  { field: 'locationName', headerName: 'Location Name', sortable: true, width: 300 },
  {
    field: 'inTransitVolume',
    headerName: 'Intransit Vol. (bbls)',
    sortable: true,
    valueFormatter: params => {
      const value = Number(params.value);
      if (params.value === null || params.value === undefined || Number.isNaN(value)) {
        return '';
      }
      return formatNumber(value.toFixed(0));
    },
    width: 200,
  },
  {
    field: 'availableVolume',
    headerName: 'Available Vol. (bbls)',
    sortable: true,
    valueFormatter: params => {
      const value = Number(params.value);
      if (params.value === null || params.value === undefined || Number.isNaN(value)) {
        return '';
      }
      return formatNumber(value.toFixed(0));
    },
    width: 200,
  },
  {
    field: 'totalVolume',
    headerName: 'Total Vol. (bbls)',
    sortable: true,
    type: 'number',
    valueFormatter: params => {
      const value = Number(params.value);
      if (params.value === null || params.value === undefined || Number.isNaN(value)) {
        return '';
      }
      return formatNumber(value.toFixed(0));
    },
    width: 200,
  },
  {
    field: 'loadableVolume',
    headerName: 'Loadable Vol. (bbls)',
    sortable: true,
    valueFormatter: params => {
      const isFooter = params.id === 'auto-generated-group-footer-root';
      if (isFooter) {
        return '';
      }
      const value = Number(params.value);
      if (params.value === null || params.value === undefined || Number.isNaN(value)) {
        return '-';
      }
      return formatNumber(value.toFixed(0));
    },
    width: 200,
  },
  {
    field: 'daysSupply',
    headerName: 'Days Supply',
    sortable: true,
    valueFormatter: params => {
      const value = Number(params.value);
      if (params.value === null || params.value === undefined || Number.isNaN(value)) {
        return '';
      }
      return formatNumber(value.toFixed(0));
    },
    width: 150,
  },
  {
    field: 'terminalAllocationFlag',
    headerName: 'Allocation',
    renderCell: params => {
      const isActive = params.row.terminalAllocationFlag === 'ACTIVE';
      return (
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
          {isActive && (
            <Tooltip placement="top" title="Terminal is on allocation.">
              <span>
                <CustomCheckIcon sx={{ color: 'blue', fontSize: 20 }} />
              </span>
            </Tooltip>
          )}
        </Box>
      );
    },
    sortable: true,
    valueGetter: params => params.row.terminalAllocationFlag,
    width: 150,
  },
];

export default toLocationColumns;
